import { useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatTime, fromZonedInput, toZonedInput } from '@/lib/datetime'
import { useApiErrorText } from '@/lib/staffGate'

import { useContests, usePlanChange, usePlanContext, useSegments, type PlanChange } from './api'
import { SegmentEditor, segmentsByItem } from './cockpit/Segments'
import {
  durationMinutes,
  nextScheduled,
  plannedEnd,
  planWarnings,
  shiftCount,
  suggestedStart,
  type PlanWarning,
} from './plan'
import type { ConductorItem, PlanActivity, PlanEvent, PlanStepFields } from './types'

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary'
const LABEL = 'block text-caption text-content-secondary'
const MAX_MINUTES = 600
// Les mêmes bornes que le serveur : la nuit de l'événement, ±12 h.
const WINDOW_MS = 12 * 3600_000
// Les retards d'une soirée se comptent par cinq minutes ; le champ libre
// reste là pour le reste.
const SHIFT_PRESETS = [-15, -5, 5, 10, 15, 30]
// Les étapes que le MC peut rattacher à une animation : un tour de concours
// ou le gâteau gardent le lien que l'administration leur a donné.
const ACTIVITY_KINDS = new Set(['slot', 'activity', 'custom'])

/**
 * « Préparer le déroulé » — la feuille de route du maître de cérémonie.
 *
 * Même table que le déroulé live, la page publique et l'écran de la salle :
 * il n'y a rien à publier, une retouche se voit au prochain rafraîchissement
 * de chacun. Pensé pour un téléphone tenu d'une main : une étape se lit en
 * une ligne, se modifie en la dépliant.
 */
export function PlanEditor({
  eventId,
  items,
  can,
}: {
  eventId: string | undefined
  items: ConductorItem[]
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const context = usePlanContext(eventId, true)
  const change = usePlanChange(eventId)

  // Une seule chose ouverte à la fois : une étape en cours d'édition, un
  // décalage ou une suppression à confirmer. Deux formulaires dépliés sur un
  // téléphone, c'est ne plus savoir lequel on enregistre.
  const [open, setOpen] = useState<
    | { kind: 'edit'; id: string }
    | { kind: 'create'; afterId: string | null }
    | { kind: 'shift'; id: string }
    | { kind: 'delete'; id: string }
    | null
  >(null)

  // Les sous-parties dépliées : plusieurs à la fois, contrairement aux
  // formulaires — on prépare souvent deux étapes voisines ensemble.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const segments = useSegments(eventId, true)
  const contests = useContests(eventId, true)
  const byItem = useMemo(() => segmentsByItem(segments.data), [segments.data])
  const toggleSegments = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const warnings = useMemo(() => planWarnings(items), [items])
  const alertCount = [...warnings.values()].filter((list) =>
    list.some((warning) => warning.kind === 'overlap' || warning.kind === 'order'),
  ).length
  const end = plannedEnd(items)
  const pivot = nextScheduled(items)

  const canCreate = can('program.item.create')
  const canUpdate = can('program.item.update')
  const canDelete = can('program.item.delete')
  const canReorder = can('program.item.reorder')

  const event = context.data?.event
  const activities = context.data?.activities ?? []
  const timezone = event?.timezone ?? 'Europe/Berlin'
  const locale = i18n.language

  const run = (payload: PlanChange, after?: () => void) => {
    change.mutate(payload, {
      onSuccess: () => {
        setOpen(null)
        after?.()
      },
    })
  }

  if (context.isError) {
    return <p className="mt-8 text-sm text-state-danger">{errorText(context.error)}</p>
  }
  if (!event) {
    return <p className="mt-8 text-content-muted">{t('state.loading')}</p>
  }

  return (
    <div className="mt-8 space-y-4">
      <Card className="border-line-subtle">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="overline text-content-muted">{t('conductor.plan.plannedEnd')}</p>
            <p className="mt-1 font-mono text-heading-md tabular-nums text-champagne-300">
              {end ? formatTime(end, locale, timezone) : '—'}
            </p>
          </div>
          <p className="text-caption text-content-secondary">
            {t('conductor.plan.steps', { count: items.length })}
            {alertCount > 0 ? (
              <span className="ml-2 text-state-warning">
                · {t('conductor.plan.alerts', { count: alertCount })}
              </span>
            ) : null}
          </p>
        </div>

        {/* Le geste du soir : la scène a pris du retard, tout ce qui reste
            à venir glisse d'un bloc. */}
        {canUpdate && pivot ? (
          open?.kind === 'shift' && open.id === pivot.id ? (
            <ShiftControl
              count={shiftCount(items, pivot.id)}
              from={title(pivot, locale)}
              pending={change.isPending}
              onCancel={() => setOpen(null)}
              onShift={(minutes) => run({ kind: 'shift', id: pivot.id, minutes })}
            />
          ) : (
            <Button
              className="mt-4 w-full sm:w-auto"
              variant="outline"
              onClick={() => setOpen({ kind: 'shift', id: pivot.id })}
            >
              {t('conductor.plan.shiftRest')}
            </Button>
          )
        ) : null}
      </Card>

      {change.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {errorText(change.error)}
        </p>
      ) : null}

      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={item.id}>
            <Card
              className={cn(
                'p-4 sm:p-5',
                item.status === 'running' && 'border-2 border-ember-400',
                (item.status === 'done' || item.status === 'skipped') && 'opacity-60',
              )}
            >
              <StepSummary
                item={item}
                locale={locale}
                timezone={timezone}
                activities={activities}
                warnings={warnings.get(item.id) ?? []}
                items={items}
              />

              {open?.kind === 'edit' && open.id === item.id ? (
                <StepForm
                  event={event}
                  activities={activities}
                  initial={item}
                  pending={change.isPending}
                  onCancel={() => setOpen(null)}
                  onSubmit={(fields) => {
                    if (Object.keys(fields).length === 0) {
                      setOpen(null)
                      return
                    }
                    run({ kind: 'update', id: item.id, fields })
                  }}
                />
              ) : open?.kind === 'shift' && open.id === item.id ? (
                <ShiftControl
                  count={shiftCount(items, item.id)}
                  from={title(item, locale)}
                  pending={change.isPending}
                  onCancel={() => setOpen(null)}
                  onShift={(minutes) => run({ kind: 'shift', id: item.id, minutes })}
                />
              ) : open?.kind === 'delete' && open.id === item.id ? (
                <div className="mt-4 rounded-md border border-state-danger/40 p-3">
                  <p className="text-sm text-content-primary">
                    {t('conductor.plan.deleteConfirm', { title: title(item, locale) })}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={change.isPending}
                      onClick={() => run({ kind: 'delete', id: item.id })}
                    >
                      {t('conductor.plan.deleteYes')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
                      {t('conductor.plan.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={expanded.has(item.id) ? 'champagne' : 'outline'}
                    aria-expanded={expanded.has(item.id)}
                    onClick={() => toggleSegments(item.id)}
                  >
                    {t('conductor.segments.toggle', {
                      count: byItem.get(item.id)?.length ?? 0,
                    })}
                  </Button>
                  {canReorder ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={t('conductor.plan.moveUp')}
                        title={t('conductor.plan.moveUp')}
                        disabled={change.isPending || index === 0}
                        onClick={() => run({ kind: 'move', id: item.id, direction: 'up' })}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={t('conductor.plan.moveDown')}
                        title={t('conductor.plan.moveDown')}
                        disabled={change.isPending || index === items.length - 1}
                        onClick={() => run({ kind: 'move', id: item.id, direction: 'down' })}
                      >
                        ↓
                      </Button>
                    </>
                  ) : null}
                  {canUpdate ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpen({ kind: 'edit', id: item.id })}
                    >
                      {t('conductor.plan.edit')}
                    </Button>
                  ) : null}
                  {canUpdate && shiftCount(items, item.id) > 0 ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpen({ kind: 'shift', id: item.id })}
                    >
                      {t('conductor.plan.shiftFromHere')}
                    </Button>
                  ) : null}
                  {canCreate ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpen({ kind: 'create', afterId: item.id })}
                    >
                      {t('conductor.plan.insertAfter')}
                    </Button>
                  ) : null}
                  {/* L'étape en cours ne se retire pas : la salle la lit comme
                      « MAINTENANT ». Le serveur refuse aussi ; le bouton ne
                      fait pas semblant. */}
                  {canDelete && item.status !== 'running' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-state-danger"
                      onClick={() => setOpen({ kind: 'delete', id: item.id })}
                    >
                      {t('conductor.plan.delete')}
                    </Button>
                  ) : null}
                </div>
              )}

              {expanded.has(item.id) ? (
                <SegmentEditor
                  eventId={eventId}
                  itemId={item.id}
                  segments={byItem.get(item.id) ?? []}
                  contests={contests.data ?? []}
                  can={can}
                />
              ) : null}
            </Card>

            {open?.kind === 'create' && open.afterId === item.id ? (
              <Card className="mt-3 border-champagne-400/40 p-4 sm:p-5">
                <p className="overline text-content-muted">{t('conductor.plan.newStep')}</p>
                <StepForm
                  event={event}
                  activities={activities}
                  initial={null}
                  defaultStart={suggestedStart(items, item.id)}
                  pending={change.isPending}
                  onCancel={() => setOpen(null)}
                  onSubmit={(fields) =>
                    run({ kind: 'create', fields: fields as PlanStepFields, afterId: item.id })
                  }
                />
              </Card>
            ) : null}
          </li>
        ))}
      </ol>

      {canCreate ? (
        open?.kind === 'create' && open.afterId === null ? (
          <Card className="border-champagne-400/40 p-4 sm:p-5">
            <p className="overline text-content-muted">{t('conductor.plan.newStep')}</p>
            <StepForm
              event={event}
              activities={activities}
              initial={null}
              defaultStart={suggestedStart(items, null) ?? event.starts_at}
              pending={change.isPending}
              onCancel={() => setOpen(null)}
              onSubmit={(fields) =>
                run({ kind: 'create', fields: fields as PlanStepFields, afterId: null })
              }
            />
          </Card>
        ) : (
          <Button
            className="w-full sm:w-auto"
            onClick={() => setOpen({ kind: 'create', afterId: null })}
          >
            {t('conductor.plan.add')}
          </Button>
        )
      ) : null}
    </div>
  )
}

function StepSummary({
  item,
  items,
  locale,
  timezone,
  activities,
  warnings,
}: {
  item: ConductorItem
  items: ConductorItem[]
  locale: string
  timezone: string
  activities: PlanActivity[]
  warnings: PlanWarning[]
}) {
  const { t } = useTranslation()
  const minutes = durationMinutes(item)
  const activity =
    item.kind === 'activity' ? activities.find((row) => row.id === item.ref_id) : undefined
  const nameOf = (id: string) => {
    const other = items.find((row) => row.id === id)
    return other ? title(other, locale) : '—'
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-sm tabular-nums text-champagne-300">
          {item.starts_at ? formatTime(item.starts_at, locale, timezone) : '—'}
          {item.ends_at ? ` → ${formatTime(item.ends_at, locale, timezone)}` : ''}
        </span>
        {minutes !== null ? (
          <span className="text-caption text-content-muted">
            {t('conductor.plan.minutes', { count: minutes })}
          </span>
        ) : null}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <h3 className="font-display text-heading-sm text-content-primary">{title(item, locale)}</h3>
        {!item.is_public ? <Badge>{t('conductor.internal')}</Badge> : null}
        {item.status === 'running' ? <Badge tone="ember">{t('program.running')}</Badge> : null}
        {item.status === 'done' ? <Badge tone="success">{t('conductor.done')}</Badge> : null}
        {item.status === 'skipped' ? <Badge>{t('program.skipped')}</Badge> : null}
        {activity ? <Badge tone="champagne">{text(activity.name_i18n, locale)}</Badge> : null}
      </div>
      {item.host || item.location ? (
        <p className="mt-1 text-caption text-content-muted">
          {[item.host, item.location].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {item.notes ? (
        <p className="mt-1 whitespace-pre-line text-caption italic text-content-secondary">
          {item.notes}
        </p>
      ) : null}
      {warnings.length > 0 ? (
        <ul className="mt-2 space-y-0.5 text-caption">
          {warnings.map((warning, index) => (
            <li
              key={index}
              className={
                warning.kind === 'overlap' || warning.kind === 'order'
                  ? 'text-state-warning'
                  : 'text-content-muted'
              }
            >
              {warning.kind === 'overlap'
                ? t('conductor.plan.warn.overlap', {
                    count: warning.minutes,
                    title: nameOf(warning.with),
                  })
                : warning.kind === 'order'
                  ? t('conductor.plan.warn.order', { title: nameOf(warning.with) })
                  : warning.kind === 'gap'
                    ? t('conductor.plan.warn.gap', { count: warning.minutes })
                    : t('conductor.plan.warn.untimed')}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * Créer ou modifier une étape.
 *
 * En modification, seuls les champs **changés** partent au serveur : une étape
 * réglée autrefois hors des bornes, ou liée au concours, reste modifiable sur
 * son libellé sans qu'on lui redemande ce qu'on n'a pas touché.
 */
function StepForm({
  event,
  activities,
  initial,
  defaultStart,
  pending,
  onSubmit,
  onCancel,
}: {
  event: PlanEvent
  activities: PlanActivity[]
  initial: ConductorItem | null
  defaultStart?: string | null
  pending: boolean
  onSubmit: (fields: Partial<PlanStepFields>) => void
  onCancel: () => void
}) {
  const { t, i18n } = useTranslation()
  const zone = event.timezone
  const startInitial = toZonedInput(initial ? initial.starts_at : (defaultStart ?? null), zone)
  const durationInitial = initial ? durationMinutes(initial) : null
  const activityInitial = initial?.kind === 'activity' ? (initial.ref_id ?? '') : ''

  const [start, setStart] = useState(startInitial)
  const [duration, setDuration] = useState(
    durationInitial === null ? '' : String(durationInitial),
  )
  const [titles, setTitles] = useState<Record<string, string>>(() => ({
    ...Object.fromEntries(event.locales.map((code) => [code, ''])),
    ...(initial?.title_i18n ?? {}),
  }))
  const [activityId, setActivityId] = useState(activityInitial)
  const [host, setHost] = useState(initial?.host ?? '')
  const [location, setLocation] = useState(initial?.location ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [isPublic, setIsPublic] = useState(initial?.is_public ?? true)
  const [error, setError] = useState<string | null>(null)

  // La langue de l'écran d'abord : c'est celle que le MC corrige. Les autres
  // restent sous ses yeux, la langue par défaut marquée obligatoire.
  const orderedLocales = [
    ...event.locales.filter((code) => code === i18n.language),
    ...event.locales.filter((code) => code !== i18n.language),
  ]
  const canLinkActivity = !initial || ACTIVITY_KINDS.has(initial.kind)

  const pickActivity = (id: string) => {
    setActivityId(id)
    const activity = activities.find((row) => row.id === id)
    if (!activity) return
    // Une animation choisie sur une étape encore vide la remplit : son nom
    // et sa durée prévue. Rien n'est écrasé si le MC a déjà saisi.
    if (Object.values(titles).every((value) => !value.trim())) {
      setTitles((current) => ({ ...current, ...activity.name_i18n }))
    }
    if (!duration && activity.duration_minutes) setDuration(String(activity.duration_minutes))
  }

  const submit = (formEvent: FormEvent) => {
    formEvent.preventDefault()
    setError(null)

    const cleanTitles = Object.fromEntries(
      Object.entries(titles)
        .map(([code, value]) => [code, value.trim()] as const)
        .filter(([, value]) => value),
    )
    if (!cleanTitles[event.default_locale]) {
      setError(t('conductor.plan.error.title', { locale: event.default_locale.toUpperCase() }))
      return
    }

    const startsAt = start ? fromZonedInput(start, zone) : null
    if (start && !startsAt) {
      setError(t('conductor.plan.error.start'))
      return
    }
    if (
      startsAt &&
      start !== startInitial &&
      Math.abs(Date.parse(startsAt) - Date.parse(event.starts_at)) > WINDOW_MS
    ) {
      setError(t('conductor.plan.error.window'))
      return
    }

    let minutes: number | null = null
    if (duration.trim()) {
      minutes = Number(duration)
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > MAX_MINUTES) {
        setError(t('conductor.plan.error.duration', { max: MAX_MINUTES }))
        return
      }
      if (!startsAt) {
        setError(t('conductor.plan.error.durationNeedsStart'))
        return
      }
    }

    const fields: PlanStepFields = {
      title_i18n: cleanTitles,
      starts_at: startsAt,
      duration_minutes: minutes,
      activity_id: activityId || null,
      host: host.trim() || null,
      location: location.trim() || null,
      notes: notes.trim() || null,
      is_public: isPublic,
    }
    if (!initial) {
      onSubmit(fields)
      return
    }

    const changed: Partial<PlanStepFields> = {}
    if (!sameText(cleanTitles, initial.title_i18n)) {
      changed.title_i18n = cleanTitles
    }
    if (start !== startInitial) changed.starts_at = startsAt
    if (minutes !== durationInitial) changed.duration_minutes = minutes
    if (canLinkActivity && activityId !== activityInitial) changed.activity_id = activityId || null
    if ((initial.host ?? '') !== host.trim()) changed.host = fields.host
    if ((initial.location ?? '') !== location.trim()) changed.location = fields.location
    if ((initial.notes ?? '') !== notes.trim()) changed.notes = fields.notes
    if (initial.is_public !== isPublic) changed.is_public = isPublic
    onSubmit(changed)
  }

  return (
    <form className="mt-4 space-y-4" onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_8rem]">
        <label className={LABEL}>
          {t('conductor.plan.field.start')}
          <input
            type="datetime-local"
            value={start}
            onChange={(change) => setStart(change.target.value)}
            className={INPUT}
          />
        </label>
        <label className={LABEL}>
          {t('conductor.plan.field.duration')}
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={MAX_MINUTES}
            step={1}
            value={duration}
            onChange={(change) => setDuration(change.target.value)}
            className={INPUT}
          />
        </label>
      </div>

      <fieldset className="space-y-3">
        <legend className={LABEL}>{t('conductor.plan.field.title')}</legend>
        {orderedLocales.map((code, index) => (
          <label key={code} className="block text-caption text-content-muted">
            {code.toUpperCase()}
            {code === event.default_locale ? ` · ${t('conductor.plan.required')}` : ''}
            <input
              value={titles[code] ?? ''}
              autoFocus={index === 0 && !initial}
              maxLength={200}
              onChange={(change) =>
                setTitles((current) => ({ ...current, [code]: change.target.value }))
              }
              className={INPUT}
            />
          </label>
        ))}
      </fieldset>

      {canLinkActivity ? (
        <label className={LABEL}>
          {t('conductor.plan.field.activity')}
          <select
            value={activityId}
            onChange={(change) => pickActivity(change.target.value)}
            className={INPUT}
          >
            <option value="">{t('conductor.plan.noActivity')}</option>
            {activities
              .filter((row) => row.is_active || row.id === activityInitial)
              .map((row) => (
                <option key={row.id} value={row.id}>
                  {text(row.name_i18n, i18n.language)}
                </option>
              ))}
          </select>
        </label>
      ) : (
        <p className="text-caption text-content-muted">{t('conductor.plan.linkedElsewhere')}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className={LABEL}>
          {t('conductor.plan.field.host')}
          <input
            value={host}
            maxLength={120}
            onChange={(change) => setHost(change.target.value)}
            className={INPUT}
          />
        </label>
        <label className={LABEL}>
          {t('conductor.plan.field.location')}
          <input
            value={location}
            maxLength={120}
            onChange={(change) => setLocation(change.target.value)}
            className={INPUT}
          />
        </label>
      </div>

      <label className={LABEL}>
        {t('conductor.plan.field.notes')}
        <textarea
          rows={3}
          value={notes}
          maxLength={2000}
          onChange={(change) => setNotes(change.target.value)}
          className={cn(INPUT, 'h-auto py-2')}
        />
      </label>

      <label className="flex items-center gap-3 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(change) => setIsPublic(change.target.checked)}
          className="h-5 w-5 accent-champagne-400"
        />
        {t('conductor.plan.field.public')}
      </label>

      {error ? (
        <p role="alert" className="text-sm text-state-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={pending}>
          {t('conductor.plan.save')}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('conductor.plan.cancel')}
        </Button>
      </div>
    </form>
  )
}

/**
 * Décaler d'un bloc — choisir un nombre de minutes, puis confirmer.
 *
 * Exporté pour le déroulé live : le retard mesuré y est proposé d'office
 * (`initial`), le MC n'a plus qu'à confirmer.
 */
export function ShiftControl({
  count,
  from,
  pending,
  onShift,
  onCancel,
  initial = null,
}: {
  count: number
  from: string
  pending: boolean
  onShift: (minutes: number) => void
  onCancel: () => void
  initial?: number | null
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState(initial ? String(initial) : '')
  const minutes = Number(value)
  const valid = value.trim() !== '' && Number.isInteger(minutes) && minutes !== 0 && Math.abs(minutes) <= 240

  return (
    <div className="mt-4 rounded-md border border-line p-3">
      <p className="text-sm text-content-secondary">
        {t('conductor.plan.shiftHelp', { count, title: from })}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {SHIFT_PRESETS.map((preset) => (
          <Button
            key={preset}
            size="sm"
            variant={value === String(preset) ? 'champagne' : 'outline'}
            onClick={() => setValue(String(preset))}
          >
            {preset > 0 ? `+${preset}` : preset}
          </Button>
        ))}
        <input
          type="number"
          inputMode="numeric"
          min={-240}
          max={240}
          value={value}
          aria-label={t('conductor.plan.shiftMinutes')}
          placeholder={t('conductor.plan.shiftMinutes')}
          onChange={(change) => setValue(change.target.value)}
          className="h-10 w-28 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={!valid || pending} onClick={() => onShift(minutes)}>
          {valid
            ? t('conductor.plan.shiftApply', {
                count,
                minutes: minutes > 0 ? `+${minutes}` : String(minutes),
              })
            : t('conductor.plan.shiftPick')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('conductor.plan.cancel')}
        </Button>
      </div>
    </div>
  )
}

function text(value: Record<string, string>, locale: string): string {
  return value[locale] ?? Object.values(value)[0] ?? '—'
}

function title(item: ConductorItem, locale: string): string {
  return text(item.title_i18n, locale)
}

/** Deux libellés multilingues identiques, à l'ordre des langues près. */
function sameText(a: Record<string, string>, b: Record<string, string>): boolean {
  const filled = (value: Record<string, string>) =>
    Object.entries(value).filter(([, entry]) => entry.trim())
  const left = filled(a)
  const right = filled(b)
  return left.length === right.length && left.every(([code, entry]) => b[code]?.trim() === entry)
}
